import React, { useState, useRef, useEffect } from "react";

interface TooltipProps {
  text: string | React.ReactNode;
  children: React.ReactNode;
  delay?: number;
  className?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  delay = 0,
  className = "max-w-xs",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    const id = setTimeout(() => setIsVisible(true), delay);
    setTimeoutId(id);
  };

  const handleMouseLeave = () => {
    if (timeoutId) clearTimeout(timeoutId);
    setIsVisible(false);
  };

  const handleClickInside = (e: React.MouseEvent) => {
    // Prevent the click from bubbling up and potentially closing the tooltip
    e.stopPropagation();
  };

  // Handle clicks outside the tooltip to close it
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        triggerRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isVisible]);

  return (
    <div
      ref={triggerRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-xl border border-gray-600 left-1/2 -translate-x-1/2 bottom-full mb-2 backdrop-blur-sm whitespace-normal ${className}`}
          onClick={handleClickInside}
        >
          {text}
          <div className="absolute w-3 h-3 bg-gray-900 border-r border-b border-gray-600 transform rotate-45 -bottom-1.5 left-1/2 -translate-x-1/2" />
        </div>
      )}
    </div>
  );
};

export default Tooltip;
